import { useState } from "react";
import { toast } from "sonner";
import { Siren, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage, useApi } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ALERT_LEVELS } from "@/lib/presets";
import type { Alert, Group, Screen } from "@/lib/types";
import { PageHeader } from "@/components/app/PageHeader";
import { ConfirmAction } from "@/components/app/ConfirmAction";

const DURATIONS = [{ v: "15", l: "15 minutes" }, { v: "30", l: "30 minutes" }, { v: "60", l: "1 heure" }, { v: "120", l: "2 heures" }, { v: "240", l: "4 heures" }, { v: "1440", l: "24 heures" }, { v: "none", l: "Jusqu'à l'arrêt manuel" }];

function TargetPicker({ items, selected, onToggle, prefix }: { items: { id: string; name: string }[]; selected: string[]; onToggle: (id: string) => void; prefix: string }) {
  return (
    <div className="grid max-h-48 gap-2 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/40 p-3 sm:grid-cols-2">
      {items.map((i) => (
        <label key={i.id} className="flex items-center gap-2 text-sm text-slate-300"><Checkbox data-testid={`${prefix}-${i.id}`} checked={selected.includes(i.id)} onCheckedChange={() => onToggle(i.id)} />{i.name}</label>
      ))}
      {!items.length && <p className="text-xs text-slate-500">Aucun élément.</p>}
    </div>
  );
}

function AlertForm({ onDone }: { onDone: () => void }) {
  const groups = useApi<Group[]>("/groups");
  const screens = useApi<Screen[]>("/screens");
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState<Alert["level"]>("danger");
  const [target, setTarget] = useState<Alert["target"]>("all");
  const [ids, setIds] = useState<string[]>([]);
  const [duration, setDuration] = useState("60");
  const toggle = (id: string) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const lv = ALERT_LEVELS.find((l) => l.v === level)!;

  const send = async () => {
    try {
      const { data } = await api.post("/alerts", { message, level, target, group_ids: target === "groups" ? ids : [], screen_ids: target === "screens" ? ids : [], duration_minutes: duration === "none" ? null : Number(duration) });
      toast.success(`Alerte diffusée sur ${data.screens_count} écran(s)`);
      setMessage(""); onDone();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="panel space-y-5 p-6" data-testid="alert-form">
      <div className="space-y-2"><Label>Message affiché en plein écran</Label><Textarea data-testid="alert-message-input" rows={3} maxLength={300} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ex. : Évacuation en cours, dirigez-vous vers la sortie la plus proche." className="border-slate-700 bg-slate-950/60 text-lg" /></div>
      <div className="grid gap-3 sm:grid-cols-3">
        {ALERT_LEVELS.map((l) => (
          <button key={l.v} type="button" data-testid={`alert-level-${l.v}`} onClick={() => setLevel(l.v)} className={`rounded-lg border-2 px-3 py-3 text-sm font-semibold transition-transform ${level === l.v ? "scale-[1.02] border-white" : "border-transparent opacity-70 hover:opacity-100"}`} style={{ background: l.bg, color: l.color }}>{l.label}</button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Télés visées</Label>
          <Select value={target} onValueChange={(v) => { setTarget(v as any); setIds([]); }}>
            <SelectTrigger data-testid="alert-target-select" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-800 bg-slate-900"><SelectItem value="all">Toutes les télés</SelectItem><SelectItem value="groups">Certains groupes</SelectItem><SelectItem value="screens">Certaines télés</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Durée</Label>
          <Select value={duration} onValueChange={setDuration}>
            <SelectTrigger data-testid="alert-duration-select" className="border-slate-700 bg-slate-950/60"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-800 bg-slate-900">{DURATIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {target === "groups" && <TargetPicker items={groups.data || []} selected={ids} onToggle={toggle} prefix="alert-group" />}
      {target === "screens" && <TargetPicker items={(screens.data || []).filter((s) => s.status === "active")} selected={ids} onToggle={toggle} prefix="alert-screen" />}
      <div className="flex aspect-[21/6] items-center justify-center rounded-lg p-4 text-center font-display text-xl font-bold" style={{ background: lv.bg, color: lv.color }} data-testid="alert-preview">{message || "Aperçu du message"}</div>
      <ConfirmAction testId="alert-send" title="Diffuser cette alerte maintenant ?" description="Elle remplacera tout le contenu des télés visées dès leur prochaine synchronisation." confirmLabel="Diffuser" onConfirm={send}
        trigger={<Button data-testid="btn-send-alert" disabled={!message.trim() || (target !== "all" && !ids.length)} className="w-full bg-red-600 py-6 text-base font-semibold text-white hover:bg-red-500"><Siren className="mr-2 h-5 w-5" />Diffuser l'alerte</Button>} />
    </div>
  );
}

export default function AlertsPage() {
  const { data, reload } = useApi<Alert[]>("/alerts");
  const stop = async (a: Alert) => { try { await api.post(`/alerts/${a.id}/stop`); toast.success("Alerte arrêtée — le contenu normal reprend"); reload(); } catch (e) { toast.error(errorMessage(e)); } };
  const live = (data || []).filter((a) => a.live);
  return (
    <div>
      <PageHeader eyebrow="Diffusion" title="Alerte d'urgence" description="Un message qui remplace tout le contenu des télés, en un clic." />
      {live.map((a) => (
        <div key={a.id} data-testid={`alert-live-${a.id}`} className="mb-4 flex flex-col gap-3 rounded-xl p-5 md:flex-row md:items-center" style={{ background: a.bg, color: a.color }}>
          <Siren className="h-6 w-6 flex-none animate-pulse" />
          <div className="flex-1"><p className="font-display text-lg font-bold">{a.message}</p><p className="text-xs opacity-80">En cours sur {a.screens_count} écran(s) · depuis {fmtDate(a.started_at)}{a.ends_at ? ` · fin ${fmtDate(a.ends_at)}` : " · jusqu'à l'arrêt"}</p></div>
          <Button data-testid={`alert-stop-${a.id}`} onClick={() => stop(a)} className="bg-black/80 text-white hover:bg-black"><Square className="mr-1 h-4 w-4" />Arrêter</Button>
        </div>
      ))}
      <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <AlertForm onDone={reload} />
        <div className="panel p-6">
          <p className="eyebrow mb-4">Historique</p>
          <ul className="space-y-3" data-testid="alert-history">
            {(data || []).map((a) => (
              <li key={a.id} className="flex items-start gap-3 text-sm"><span className="mt-1.5 h-2.5 w-2.5 flex-none rounded-full" style={{ background: a.bg }} /><div className="min-w-0"><p className="truncate text-slate-200">{a.message}</p><p className="text-xs text-slate-500">{fmtDate(a.started_at)} · {a.live ? "en cours" : a.stopped_at ? "arrêtée" : "terminée"} · {a.created_by}</p></div></li>
            ))}
            {!data?.length && <p className="text-sm text-slate-500">Aucune alerte diffusée.</p>}
          </ul>
        </div>
      </div>
    </div>
  );
}
